const CHECKOUT_ERROR_CODES = [
  'tokenization_failed',
  'fraud_declined',
  'fraud_check_failed',
  'invalid_payment_data',
  'invalid_payment_source',
  'payment_customer_creation_failed',
  'payment_card_creation_failed',
  'issuer_installment_failed',
  'instrument_verification_failed',
  'card_migration_failed',
  'payment_authorization_failed',
  'order_validation_failed',
  'recaptcha_failed',
  'network_failure',
  'internal_error',
] as const;

export type CheckoutErrorCode = (typeof CHECKOUT_ERROR_CODES)[number];

export interface NormalizedCheckoutError {
  errorCode: CheckoutErrorCode;
  errorDetails?: string;
}

export interface CheckoutApiError {
  error_code?: string;
  http_status?: number;
  data?: {
    error_code?: string;
  };
}

const isCheckoutErrorCode = (value: unknown): value is CheckoutErrorCode =>
  typeof value === 'string' &&
  CHECKOUT_ERROR_CODES.includes(value as CheckoutErrorCode);

const errorCodeFromApiError = (
  error: unknown,
): CheckoutErrorCode | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const apiError = error as CheckoutApiError;
  const errorCode = apiError.error_code || apiError.data?.error_code;
  return isCheckoutErrorCode(errorCode) ? errorCode : undefined;
};

const httpStatusFromApiError = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const { http_status: httpStatus } = error as CheckoutApiError;
  return typeof httpStatus === 'number' ? httpStatus : undefined;
};

const normalizeCheckoutError = ({
  error,
  fallbackCode,
  httpStatus,
}: {
  error?: unknown;
  fallbackCode: CheckoutErrorCode;
  httpStatus?: number;
}): NormalizedCheckoutError => {
  const resolvedHttpStatus = httpStatus || httpStatusFromApiError(error);
  const apiErrorCode = errorCodeFromApiError(error);
  if (apiErrorCode) {
    return {
      errorCode: apiErrorCode,
      ...(resolvedHttpStatus
        ? { errorDetails: `http_status: ${resolvedHttpStatus}` }
        : {}),
    };
  }

  if (resolvedHttpStatus && resolvedHttpStatus >= 500) {
    return {
      errorCode: 'internal_error',
      errorDetails: `http_status: ${resolvedHttpStatus}`,
    };
  }

  if (resolvedHttpStatus) {
    return {
      errorCode: fallbackCode,
      errorDetails: `http_status: ${resolvedHttpStatus}`,
    };
  }

  if (error instanceof Error && error.name === 'VerifyBuyerError') {
    return { errorCode: 'instrument_verification_failed' };
  }

  return { errorCode: fallbackCode };
};

const normalizeCheckoutResponseError = async ({
  response,
  fallbackCode,
}: {
  response: Response;
  fallbackCode: CheckoutErrorCode;
}): Promise<NormalizedCheckoutError> => {
  let error: unknown;
  try {
    error = await response.clone().json();
  } catch {
    // The RUM detail is deliberately limited to status; raw response text can contain sensitive data.
  }

  return normalizeCheckoutError({
    error,
    fallbackCode,
    httpStatus: response.status,
  });
};

export { normalizeCheckoutError, normalizeCheckoutResponseError };
